import { DoublyLinkedList } from "./DoublyLinkedList";
/**
 * A segmented priority queue that maintains a set of 'safe' objects and a set of 'evict-able' objects that are sorted
 * in order of time since last flagged as 'safe'.
 *
 * EvictionQueue tracks nodes as:
 * - 'safe', which means it can not be evicted under any circumstances
 * - 'evict-able', which means it can be evicted if needed
 *
 * This data structure uses a doubly linked list with a special 'sentinel' node that separates the list into two subsets
 * to the left and right of the sentinel node. Anything to the right ('next' chain) of the sentinel is 'safe', and
 * anything to the left is 'evict-able'.
 *
 * Every frame 'newFrame' is called, which moves 'sentinel' to the tail of the list. This marks all nodes as
 * 'evict-able'. After 'newFrame' is called live nodes are 'touched' using 'this.touch'. Touching a node moves it to be
 * immediately after 'sentinel' which marks it as 'safe'. After touching live nodes, 'evict-able' nodes can be popped
 * from the head of the list to be evicted.
 *
 * This usage pattern will naturally sort the list in order of time last touched, from most recently touched at the tail
 * to longest since touched at the head. This means popping from the head will give us the best candidates to evict
 * based on age.
 */
export class EvictionQueue {
    constructor() {
        /**
         * Linked list that forms the backing for the priority queue. Sorted (ish) by LRU at head and MRU at tail.
         * @private
         */
        this.list = new DoublyLinkedList();
        /**
         * A flag that can be activated to force clearing all 'evict-able' nodes regardless of what the 'evictCondition'
         * returns.
         * @private
         */
        this._shouldTrim = false;
        this._sentinel = this.list.add();
    }
    /**
     * Returns the number of nodes in the queue
     */
    get length() {
        return this.list.length - 1; // -1 because the sentinel doesn't count as a node in the queue
    }
    /**
     * The 'sentinel' node that is used as a bisector to track live/old nodes.
     *
     * Any node after 'sentinel' is considered live. Any node previous to 'sentinel' is considered old and candidate for
     * eviction.
     */
    get sentinel() {
        return this._sentinel;
    }
    /**
     * Queue a forced 'trim' operation.
     */
    queueTrim() {
        this._shouldTrim = true;
    }
    /**
     * Marks that a new frame has begun.
     *
     * This moves the sentinel to the tail of the list, marking all nodes as candidate for eviction.
     *
     * The intended use case is to mark and touch nodes every frame with the following sequence
     * - `this.newFrame() // Marks all nodes as eviction candidates`
     * - `this.touch(node) // Touch any nodes that must remain loaded`
     * - `this.evictNodes() // After updating the queue we trigger unloading tiles, starting from the oldest`
     */
    newFrame() {
        if (this.list.tail !== null) {
            this.list.splice(this.list.tail, this._sentinel);
        }
    }
    /**
     * Insert a new item with the given payload into the queue. The list node containing the data will be returned.
     * @param data
     */
    add(data) {
        const node = this.list.add(data);
        // list.add can only create a node with no data when no argument is passed, which the function signature
        // prevents.
        return node;
    }
    /**
     * Updates the given node to mark it as 'safe' until the next call to 'newFrame'.
     *
     * This will move the node inside the queue, so it also maintains the LRU order.
     * @param node The node to 'touch'
     */
    touch(node) {
        this.list.splice(this._sentinel, node);
    }
    /**
     * Evicts an individual node from the queue, unconditionally. This will *not* check if the node is 'safe'.
     * @param node The node to evict
     * @param evictCallback A callback function that will be called with the node's data after the node has been
     *                       removed (evicted) from the queue
     */
    evictNode(node, evictCallback) {
        this.list.remove(node);
        evictCallback(node.data);
    }
    /**
     * Performs a node eviction cycle. Will evict nodes from the queue in order from least recently touched to most
     * recently touched while stopping once the 'evictCondition' callback returns false for the first time, or when no
     * non-safe nodes remain if a 'trim' has been queued.
     *
     * The 'evictCallback' will be called with the data of each evicted node as they are evicted.
     *
     * The 'evictCondition' callback will be called while iterating the queue every iteration to decide when enough
     * nodes have been evicted. The callback should return true for as long as nodes should continue to be evicted. Once
     * no more nodes need to be evicted it should return false, the iteration will stop and the function will return.
     *
     * If 'trim' has been queued then the callback will ignore 'evictCondition' and will loop until all evict-able nodes
     * have been removed from the queue. 'evictCondition' will still be called, but the function will just keep
     * iterating regardless of its return value.
     *
     * @param evictCondition Callback function that will be checked prior to each eviction attempt.
     * @param evictCallback A callback function that will be called with the node's data after the node has been
     *                       removed (evicted) from the queue
     */
    evictNodes(evictCondition, evictCallback) {
        // Capture and reset the shouldTrim flag
        const shouldTrim = this._shouldTrim;
        this._shouldTrim = false;
        // Traverse the list only to the sentinel since tiles/nodes to the
        // right of the sentinel were used this frame.
        //
        // The sub-list to the left of the sentinel is ordered from LRU to MRU.
        const sentinel = this._sentinel;
        let node = this.list.head;
        while (true) {
            // If there's no node, or we've reached the sentinel then stop iterating
            if (node === null)
                break;
            if (node === sentinel)
                break;
            // If our eviction condition is no longer true, and we're not force evicting nodes we should stop
            if (!(shouldTrim || evictCondition()))
                break;
            // It is impossible for a node (other than the sentinel) to have no 'data' field, as the class interface
            // can't create a node without data. Reaching this code where 'node === sentinel' is impossible so this
            // cast is safe.
            const nodeWithData = node;
            // Evict the node
            this.evictNode(nodeWithData, evictCallback);
            // Advance to the next node in the list, ready to check if it still needs to be evicted or if we've reached
            // the sentinel.
            node = node.next;
        }
    }
}
